import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { useToast } from '@/hooks'
import { useAccountMutations } from '@/modules/account'
import {
  MembershipLimitError,
  TrialEmailAliasError,
  TrialEmailDisposableError,
} from '@/modules/errors'
import { redirectTo } from '@/utils'
import type { TrialSignUpDetails } from '../utils/onboarding'

interface StartTrialOptions {
  onEmailRefused?: (message: string) => void
  onUnexpectedFailure?: () => void
}

const isEmailRefusal = (error: Error) =>
  error instanceof TrialEmailAliasError ||
  error instanceof TrialEmailDisposableError

export const useStartTrial = ({
  onEmailRefused,
  onUnexpectedFailure,
}: StartTrialOptions = {}) => {
  const mutations = useAccountMutations()
  const navigate = useNavigate()
  const { errorToast } = useToast()
  const [isAlreadyMember, setIsAlreadyMember] = useState(false)

  // A lukita that still asks for a card answers with a Stripe checkout to finish first.
  const openTrial = (checkoutUrl: string | null) =>
    checkoutUrl ? redirectTo(checkoutUrl) : navigate({ to: '/trial/welcome' })

  const handleFailure = (error: Error) => {
    if (error instanceof MembershipLimitError) {
      setIsAlreadyMember(true)
      return
    }
    if (onEmailRefused && isEmailRefusal(error)) {
      onEmailRefused(error.message)
      return
    }
    errorToast('We could not start your trial', error)
    onUnexpectedFailure?.()
  }

  const startTrial = (details: TrialSignUpDetails) =>
    mutations.startTrial(details).then(openTrial).catch(handleFailure)

  return { startTrial, isAlreadyMember }
}
